import { useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  ContextLine,
  Copyable,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateWithDelta,
  PrimaryFullScreenSpinner,
  StatusUI,
  Txt,
} from "@repo/ui";
import { NotFoundError, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { compareAsc } from "date-fns";
import { Suspense } from "react";
import { DetailRow, DetailSubRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type { AdminFundUnitOrderTransferRowFragment } from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute("/__auth/fund-unit-orders_/$id")({
  head: () => opsHead("Fund Unit Order"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { id } = Route.useParams();

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminFundUnitOrderDetail($id: ID!) {
        node(id: $id) {
          ... on FundUnitOrder {
            id
            shortRef
            orderKind
            currencyAmount {
              amount
              currency
            }
            custodyAccount {
              id
              shortRef
              currency
              product {
                id
                name
                internalName
                provider {
                  id
                  name
                  logo {
                    id
                    url
                  }
                }
              }
            }
            transfers {
              ...AdminFundUnitOrderTransferRow
            }
          }
        }
      }

      fragment AdminFundUnitOrderTransferRow on Transfer {
        id
        shortRef
        requestedForDate
        currencyAmount {
          amount
          currency
        }
        status
        sourceAsset {
          ...AssetCardFields
        }
        destinationAccount {
          ...AssetCardFields
          ...BeneficiaryAssetCardFields
        }
      }
    `),
    {
      variables: { id },
    },
  );

  const order = data.node;
  if (!order || order.__typename !== "FundUnitOrder") {
    throw new NotFoundError();
  }

  useOpsTitle(`Fund Unit Order #${order.shortRef}`);

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title={`BlackRock Unit Order #${order.shortRef}`}
        subtitle={prettyPrintEnum(order.orderKind)}
        contextLine={
          <ContextLine
            current={`#${order.shortRef}`}
            ancestors={[{ label: "Fund unit order", path: "/" }]}
          />
        }
      />

      <div className="flex flex-col gap-10 rounded-lg border bg-white p-12 shadow-surface">
        <section className="flex flex-col gap-6">
          <Txt variant="card-title">Order details</Txt>
          <div className="flex w-full flex-col gap-0">
            <DetailRow
              label="ID"
              value={
                <Copyable tooltipSide="right">
                  <span className="font-mono">{order.id}</span>
                </Copyable>
              }
            />
            <DetailRow label="Short Ref" value={`#${order.shortRef}`} />
            <DetailRow label="Kind" value={prettyPrintEnum(order.orderKind)} />
            <DetailRow
              label="Amount"
              value={
                order.currencyAmount ? (
                  <CurrencyAmountUI {...order.currencyAmount} />
                ) : (
                  "No amount recorded."
                )
              }
            />
            <DetailRow label="Custody Account">
              {order.custodyAccount ? (
                <>
                  <DetailSubRow
                    label="Short Ref"
                    value={
                      <Link to={`/custody-accounts/${order.custodyAccount.id}`}>
                        #{order.custodyAccount.shortRef}
                      </Link>
                    }
                  />
                  <DetailSubRow
                    label="Currency"
                    value={order.custodyAccount.currency}
                  />
                  <DetailSubRow
                    label="Product"
                    value={
                      order.custodyAccount.product?.internalName ??
                      "No product recorded."
                    }
                  />
                  <DetailSubRow
                    label="Provider"
                    value={
                      order.custodyAccount.product?.provider?.name ??
                      "No provider recorded."
                    }
                  />
                </>
              ) : (
                "No custody account recorded."
              )}
            </DetailRow>
          </div>
        </section>

        <section className="flex flex-col gap-6">
          <Txt variant="card-title">Transfers</Txt>
          <DataTable
            data={order.transfers ?? []}
            columns={transferColumns}
            pageSize={10_000}
            hidePagination={true}
            className="overflow-hidden border"
            initialColumnVisibility={{
              id: false,
            }}
          />
        </section>
      </div>
    </AdminPageContainer>
  );
}

const transferColumns: ColumnDef<
  AdminFundUnitOrderTransferRowFragment & { id: string }
>[] = [
  {
    header: "ID",
    accessorKey: "id",
    cell: () => null,
  },
  {
    accessorKey: "requestedForDate",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => <DateWithDelta date={row.original.requestedForDate} />,
    sortingFn: (a, b) =>
      compareAsc(a.original.requestedForDate, b.original.requestedForDate),
  },
  {
    accessorKey: "shortRef",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Transfer" />
    ),
    cell: ({ row }) => (
      <Link to={`/transfers/${row.original.id}`}>#{row.original.shortRef}</Link>
    ),
  },
  {
    accessorKey: "sourceAsset",
    header: "From",
    cell: ({ row }) =>
      row.original.sourceAsset ? (
        <AccountCard
          subText="BUSINESS_ENTITY"
          account={row.original.sourceAsset}
        />
      ) : (
        "No source asset"
      ),
  },
  {
    accessorKey: "destinationAccount",
    header: "To",
    cell: ({ row }) =>
      row.original.destinationAccount ? (
        <AccountCard
          subText="BUSINESS_ENTITY"
          account={row.original.destinationAccount}
        />
      ) : (
        "No destination asset"
      ),
  },
  {
    id: "amount",
    accessorFn: () => undefined,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Amount"
        align="right"
        className="flex flex-row justify-end pr-2"
      />
    ),
    cell: ({ row }) => {
      if (!row.original.currencyAmount) {
        return null;
      }

      return (
        <div className="text-right">
          <CurrencyAmountUI {...row.original.currencyAmount} />
        </div>
      );
    },
  },
  {
    id: "status",
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) =>
      row.original.status ? <StatusUI status={row.original.status} /> : null,
  },
];
